import React from 'react';
import { AlignLeft } from 'lucide-react';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export type MontosResumenTipo = 'neutro' | 'positivo' | 'negativo';

export interface MontosResumenProps {
  /** Etiqueta de la fila. Ej: "Apertura", "+ Ingresos", "− Egresos" */
  label: string;
  /** Monto a mostrar */
  monto: number;
  /** Color semántico del monto */
  tipo?: MontosResumenTipo;
  /**
   * Si se pasa, muestra el badge con el conteo y habilita el botón de detalle.
   */
  cantidadMedios?: number;
  /** Variante de color del badge según el tipo */
  badgeTipo?: 'ingresos' | 'egresos';
  /** Callback al presionar el badge de detalle */
  onVerDetalle?: () => void;
  /** Clase adicional para el contenedor */
  className?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatMonto = (monto: number): string =>
  `S/ ${monto.toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const montoColorClass: Record<MontosResumenTipo, string> = {
  neutro:   'text-primary',
  positivo: 'text-success',
  negativo: 'text-error',
};

// ─── Componente ───────────────────────────────────────────────────────────────

export const MontosResumen: React.FC<MontosResumenProps> = ({
  label,
  monto,
  tipo = 'neutro',
  cantidadMedios,
  badgeTipo = 'ingresos',
  onVerDetalle,
  className = '',
}) => {
  const tieneDetalle = cantidadMedios !== undefined && onVerDetalle;

  const badgeClasses =
    badgeTipo === 'ingresos'
      ? 'bg-info-bg text-info border border-info-border hover:bg-info-bg/80'
      : 'bg-error-bg text-error border border-error-border hover:bg-error-bg/80';

  return (
    <div
      className={[
        'flex items-center justify-between',
        'px-3.5 py-2.5',
        'border-b border-muted last:border-b-0',
        className,
      ].join(' ')}
    >
      {/* Izquierda: label + badge */}
      <div className="flex items-center gap-2">
        <span className="text-[13px] text-secondary">{label}</span>

        {tieneDetalle && (
          <button
            type="button"
            onClick={onVerDetalle}
            className={[
              'inline-flex items-center gap-1',
              'text-[10px] font-medium',
              'px-2 py-0.5 rounded-full',
              'transition-colors cursor-pointer',
              badgeClasses,
            ].join(' ')}
            aria-label={`Ver desglose — ${cantidadMedios} medios de pago`}
          >
            <AlignLeft size={10} aria-hidden="true" />
            {cantidadMedios} {cantidadMedios === 1 ? 'medio' : 'medios'}
          </button>
        )}
      </div>

      {/* Derecha: monto */}
      <span
        className={[
          'text-[13px] font-medium',
          montoColorClass[tipo],
        ].join(' ')}
      >
        {formatMonto(monto)}
      </span>
    </div>
  );
};

MontosResumen.displayName = 'MontosResumen';


